import { CollectedSurcharge, InvoiceDetail } from "../../../domain/invoicing/invoice"
import { isEmptyRichText } from "../../RichTextEditor/richTextModel"
import { collectedSurchargeLines } from "./collectedSurchargeModel"

export type LineItemViewData = {
  key: string
  description: string
  descriptionHtml: string | null
  amount: number | null
  qboDescription: string
  qboAmount: number | null
  amountLabel: string
}

export const buildLineItemViewData = (detail: InvoiceDetail, currentBalance: number, paid: CollectedSurcharge): LineItemViewData[] => {
  const { invoice, quickBooksInvoice } = detail
  const data: LineItemViewData[] = []
  const invoiceLines = invoice.lineItems.filter(x => x.type !== 'salesTaxCharge')
  const qboLines = quickBooksInvoice?.lineItems
    .filter(x => x.type === 'salesItemLineDetail' || x.type === 'discountLineDetail')
    ?? []
  const maxLines = Math.max(invoiceLines.length, qboLines.length)

  const calculateQboAmount = (isDiscount: boolean, amount: number | null | undefined) => {
    if (amount === null || amount === undefined) {
      return null
    }
    return isDiscount ? -amount : amount
  }

  for (let i = 0; i < maxLines; i++) {
    const isDiscount = qboLines[i]?.type === 'discountLineDetail'
    const defaultQboDescription = isDiscount ? 'Discount' : ''

    data.push({
      key: `line-${i}`,
      description: invoiceLines[i]?.description ?? "",
      descriptionHtml: invoiceLines[i]?.descriptionHtml ?? null,
      amount: invoiceLines[i]?.amount ?? null,
      qboDescription: qboLines[i]?.description ?? defaultQboDescription,
      qboAmount: calculateQboAmount(isDiscount, qboLines[i]?.amountWithoutTax),
      amountLabel: ''
    })
  }

  data.push({
    key: 'subtotal',
    description: '',
    descriptionHtml: null,
    amount: invoice.amount,
    qboDescription: '',
    qboAmount: quickBooksInvoice?.amountWithoutTax ?? null,
    amountLabel: 'Subtotal: '
  })

  if (invoice.salesTax > 0 || (quickBooksInvoice?.totalSalesTax ?? 0) > 0) {
    data.push({
      key: 'sales-tax',
      description: '',
      descriptionHtml: null,
      amount: invoice.salesTax,
      qboDescription: '',
      qboAmount: quickBooksInvoice?.totalSalesTax ?? null,
      amountLabel: 'Sales Tax: '
    })
  }

  data.push({
    key: 'total',
    description: '',
    descriptionHtml: null,
    amount: invoice.total,
    qboDescription: '',
    qboAmount: quickBooksInvoice?.totalAmount ?? null,
    amountLabel: 'Total: '
  })

  for (const line of collectedSurchargeLines(paid)) {
    data.push({
      key: line.key,
      description: '',
      descriptionHtml: null,
      amount: line.amount,
      qboDescription: '',
      qboAmount: null,
      amountLabel: `${line.label}: `
    })
  }

  data.push({
    key: 'balance',
    description: '',
    descriptionHtml: null,
    amount: currentBalance,
    qboDescription: '',
    qboAmount: quickBooksInvoice?.balance ?? null,
    amountLabel: 'Balance: '
  })

  return data
}

export const descriptionFirstLine = (description: string) => {
  const index = description.indexOf('\n')
  return index === -1 ? description : description.substring(0, index)
}

export type DescriptionDetails =
  | { kind: 'text', text: string }
  | { kind: 'html', html: string }

export type DescriptionDisplay = {
  title: string
  details: DescriptionDetails | null
}

export const buildDescriptionDisplay = (description: string, descriptionHtml: string | null): DescriptionDisplay => {
  const trimmed = description.trim()
  const title = descriptionFirstLine(trimmed)

  if (descriptionHtml && !isEmptyRichText(descriptionHtml)) {
    return { title, details: { kind: 'html', html: descriptionHtml } }
  }

  const remainder = trimmed.substring(title.length).trim()
  if (remainder.length > 0) {
    return { title, details: { kind: 'text', text: remainder } }
  }

  return { title, details: null }
}
